import {
  ChangeDetectionStrategy,
  Component,
  computed,
  EventEmitter,
  inject,
  input,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';

import { ButtonComponent } from '../../button/button.component';
import { PriceComponent } from '../../price/price.component';
import { FormsModule } from '@angular/forms';
import { SvgIconComponent } from '@ngneat/svg-icon';
import { TranslocoDirective } from '@jsverse/transloco';
import { Variant } from '../../../pages/product/product-page/product.types';
import { ProductOptionsComponent } from './product-options/product-options.component';
import {
  OptionType,
  Property,
} from '../../../pages/product/product-list/product-list.mapper';
import { CartService } from '../../../pages/cart-page/cart.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { uniqueByKey } from '../../../helpers/array';
import { StateService } from '../../../services/state.service';
import { AvailabilityAtShowroomsComponent } from './availability-at-showrooms/availability-at-showrooms.component';
import { DataLayerService } from '../../../services/data-layer.service';
import { AuthenticationService } from '../../../services/authentication.service';
import { WishlistService } from '../../../services/wishlist.service';

@Component({
  selector: 'app-product-details',
  imports: [
    ButtonComponent,
    PriceComponent,
    FormsModule,
    SvgIconComponent,
    TranslocoDirective,
    ProductOptionsComponent,
    AvailabilityAtShowroomsComponent,
  ],
  templateUrl: './product-details.component.html',
  styleUrls: ['./product-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductDetailsComponent implements OnInit, OnDestroy {
  private _variants: Variant[] = [];
  private cart = inject(CartService);
  private store = inject(StateService);
  private readonly dataLayer = inject(DataLayerService);
  private readonly wishlistService = inject(WishlistService);
  readonly auth = inject(AuthenticationService);

  locale = this.store.getActiveLocale();
  error = toSignal(this.cart.error$);

  optionTypes: OptionType[] = [];
  model: Property | undefined;

  sizesUrls = {
    en: 'https://cdn.yesjewellery.com/docs/legal/Ring-and-wedding-ring-sizes-YESjewelleryEN.pdf',
    sk: 'https://cdn.yesjewellery.com/docs/legal/Velkosti-prstenov-a-snubnych-prstenov-YESjewellerySK.pdf',
    cs: 'https://cdn.yesjewellery.com/docs/legal/Velikosti-prstenu-a-snubnich-prstenu-YESjewelleryCZ.pdf',
  };

  sizeUrl = this.sizesUrls['en'];

  hideOnEnLocale = true;

  @Input({ required: true })
  set variants(v: Variant[]) {
    this._variants = v;
    this.parseOptions();
  }

  activeVariant = input.required<Variant>();

  isProductInWishlist = computed(() => {
    const variantId = this.activeVariant().variantId;

    return this.wishlistService
      .items()
      .some((item) => item.variantId.toString() === variantId.toString());
  });

  isButtonDisabled = computed(() => {
    const av = this.activeVariant();

    return (
      !av.available || !av.inStock || !!this.error()?.['addItem'] || av.price.current <= 0
    );
  });

  @Output()
  activeVariantChange = new EventEmitter<Variant>();

  @Output()
  addToCart = new EventEmitter<Variant>();

  ngOnInit(): void {
    this.store.activeLocale$.subscribe((locale) => {
      if (locale === 'en' || locale === 'sk' || locale === 'cs') {
        this.sizeUrl = this.sizesUrls[locale];

        this.hideOnEnLocale = locale !== 'en';
      }
    });

    this.model = this.activeVariant().properties.find((p) => p.name === 'Model');
  }

  onCheckAvailability(): void {
    this.dataLayer.onCheckAvailability(this.activeVariant());
  }

  onSubmit(): void {
    if (this.isButtonDisabled()) {
      return;
    }

    this.addToCart.emit(this.activeVariant());
  }

  addToWishlist(event: Event): void {
    event.stopPropagation();

    this.wishlistService.add(this.activeVariant());
  }

  removeFromWishlist(event: Event): void {
    event.stopPropagation();

    this.wishlistService.remove(this.activeVariant());
  }

  parseOptions(): void {
    const optionTypes = new Map<string | number, OptionType>();

    this._variants.forEach((variant) => {
      variant.optionTypes.forEach((type) => {
        if (!type.values.length) {
          return;
        }

        let clone;

        if (typeof structuredClone === 'function') {
          clone = structuredClone(type);
        } else {
          clone = JSON.parse(JSON.stringify(type));
        }

        if (!optionTypes.has(type.id)) {
          optionTypes.set(type.id, clone);
        } else {
          optionTypes.get(type.id)?.values.push(...type.values);
        }
      });
    });

    const unsortedOptionTypes = Array.from(optionTypes.values());
    unsortedOptionTypes.forEach((ot) => {
      ot.values = uniqueByKey(ot.values).sort((a, b) => {
        return a.position - b.position;
      });
    });

    this.optionTypes = unsortedOptionTypes.sort((a, b) => a.position - b.position);
  }

  onVariantChange(
    variantOption: {
      typeId: number | string;
      valueId: number | string;
    }[],
  ): void {
    const variant = this._variants.find((obj) => {
      return variantOption.every((option) => {
        return obj.optionValues.some(
          (optionValue) =>
            optionValue.optionTypeId === option.typeId &&
            optionValue.id === option.valueId,
        );
      });
    });

    if (!variant) {
      const clone =
        typeof structuredClone === 'function'
          ? structuredClone(this.activeVariant)
          : JSON.parse(JSON.stringify(this.activeVariant));

      clone.inStock = false;
      clone.price.current = 0;
      clone.price.original = 0;

      this.activeVariantChange.emit(clone);

      return;
    }

    this.activeVariantChange.emit(variant);
  }

  ngOnDestroy(): void {
    this.cart.clearError();
  }
}
